/**
 * @module FluxStore
 */

import { register } from "./Dispatcher";
import { UpdateBuilder } from "./StoreUpdater";
import StoreLoggerMiddleware from "../middleware/StoreLogger";
import { EventEmitter } from "events";
import { fromStorage } from "../middleware/StoreLocalStorage";

/**
 * @constant
 * @type {string}
 * @default
 */
const CHANGE_EVENT = "change";

/**
 * @name ChangeStateMiddleware
 * @method
 * @description
 * Default middleware that changes the state of stores in response to action dispatches.
 * 
 * @param {String} name         name of the dispatched event.
 * @returns {Function}          next middleware to be invoked.
 */
export const ChangeStateMiddleware = name => events => action => state => next => {
  if (events.hasOwnProperty("*")) {
    return next(name, events, action, events["*"](action, state));
  }
  return next(name, events, action, events[action.actionType](action, state));
};

/**
 * Utility function to return the type of a object | function | string etc.
 * @method
 * 
 * @param {*} type 
 * @returns {string}
 */
export const getType = type => {
  return Object.prototype.toString.call(type);
};

/**
 * Utility function checking for a type being of Function.
 * @method
 * 
 * @param {*} type 
 * @returns {boolean}
 */
export const isFunc = type => getType(type) === "[object Function]";
let _middlewares = [StoreLoggerMiddleware, ChangeStateMiddleware];

/**
 * Adds function to middleware array and will be invoked for ALL application stores CHANGE_EVENTS, return new or unchanged state.
 * @method
 * @param {Array} middleware  - Array of middleware function to be called on all store dispatches
 * @returns {null}
 * @example
 * 
 * import { ApplyMiddleware } from '@selkirk-systems/StateManagment';
 * 
 * const StoreLoggerMiddleware = ( name ) => ( events ) => ( action ) => ( state ) => ( next ) => {

    //Only log outputs for 'development' environment
    if ( process.env.NODE_ENV !== "development" ) {

        return next( name, events, action, state );

    }

    const started = timer.now();
    let nextState = next( name, events, action, state );

    if ( !action.options.silent ) {

        const duration = timer.now() - started;

        console.groupCollapsed(
            `%clistener %c${name} %c(in ${duration.toFixed( 2 )} ms)`,
            bodyCSS,
            storeCSS,
            bodyCSS
        );

    }

    if ( !nextState ) {

        nextState = "State Undefined - No update or change event made";

    }

    if ( !action.options.silent ) {

        console.log( `%cprev state`, prevCSS, state );
        console.log( `%cnext state`, nextCSS, nextState );

        console.groupEnd();

    }

    return nextState;


};

ApplyMiddleware([StoreLoggerMiddleware])
*/
export const ApplyMiddleware = (middleware = []) => {
  _middlewares = middleware;
  _middlewares.push(StoreLoggerMiddleware, ChangeStateMiddleware);
};
const applyMiddleware = middlewares => (name, events, action, state) => {
  let i = -1;
  const next = function (name, events, action, state) {
    i++;
    const middleware = middlewares[i];
    if (!middleware) return state;
    return middleware(name)(events)(action)(state)(next);
  };
  return next(name, events, action, state);
};

/**
 * @typedef StoreProps
 * @prop {string} name - Name of the store
 * @prop {Object<string,function>} events - events object that responds to dispatches
 * @prop {Object} state - initial state of the store
 * @memberof module:FluxStore
*  @name StoreProps
 */

/**
 * @description
 * Factory function that returns a store to manage both domain state and user interface state.
 * 
 * @method
 * @static
 * @param {module:FluxStore.StoreProps} props
 * @returns {CreateStore#Store}
 */
const CreateStore = (/**@type {StoreProps} */{
  name,
  events,
  state
}) => {
  const initState = state;
  var _model = {};
  function setState(obj) {
    if (obj === undefined) {
      console.error("CreateStore - Attempting to set state to undefined, make sure you return state from all store functions", _model);
      return;
    }
    _model = obj;
  }
  function getState() {
    return _model;
  }
  setState(fromStorage(initState)(name, events, name));
  Object.keys(events).forEach(key => {
    const evt = events[key];
    if (evt instanceof UpdateBuilder) {
      events[key] = evt.build();
    }
  });

  /**
   * @name CreateStore#Store
   * @class
   * @extends EventEmitter
   * 
   * Manages application state
   * 
   */
  const Store = Object.assign({}, EventEmitter.prototype, {
    /**
     * Used internally by StoreLocalStorage middleware to rehydrate a store from local or session storage.
     * 
     * @name Store#_setInitialState
     * @method
     * @private
     * @param {Object} initialState 
     * @returns {null}
     */
    _setInitialState(initialState) {
      setState({
        ...getState(),
        ...initialState
      });
    },
    _setStorageName(storageName) {
      this.__STORAGE_NAME = storageName;
      setState(fromStorage(initState)(name, events, this.__STORAGE_NAME));
    },
    rehydrateFromStorage(state) {
      setState(fromStorage({
        ...initState,
        ...state
      })(name, events, this.__STORAGE_NAME));
    },
    /**
     * Gets the name of the store
     *
     * @name CreateStore#Store#getName
     * @method
     * @returns {String}
     */
    getName() {
      return name;
    },
    /**
    * Gets the current state of the store
    *
    * @name CreateStore#Store#getState
    * @method
    * @returns {Object}
    */
    getState() {
      return getState();
    },
    /**
    * Causes the store to emit it has changed
    *
    * @name CreateStore#Store#emitChange
    * @private
    * @method
    */
    emitChange() {
      this.emit(CHANGE_EVENT);
    },
    /**
    * Adds the callback function to the store and will be invoked on CHANGE_EVENTS
    * @name CreateStore#Store#addChangeListener
    * @param {Function}            callback    - function invoked when a CHANGE_EVENT is emitted
    * @method
    * @returns {null}
    */
    addChangeListener(callback) {
      //Insure we never create duplicate events
      this.removeListener(CHANGE_EVENT, callback);
      this.on(CHANGE_EVENT, callback);
    },
    /**
    * Removes the callback function from the store CHANGE_EVENTS
    *
    * @name CreateStore#Store#removeChangeListener
    * @param {function}            callback    - function removed from CHANGE_EVENTS
    * @method
    * @returns {null}
    */
    removeChangeListener(callback) {
      this.removeListener(CHANGE_EVENT, callback);
    },
    /**
     * @name CreateStore#Store#addEvent
     * @private
     * @param {string} eventName 
     * @param {function} func 
     */
    addEvent(eventName, func) {
      events[eventName] = func;
    },
    dispatcherIndex: register(function (dispatch) {
      var action = dispatch.action;
      if (action.actionType === undefined) {
        console.log("CreateStore - actionType undefined. This means that an action is dispatching using a constant that is not defined");
        return true;
      }
      if (events.hasOwnProperty(action.actionType) || events.hasOwnProperty("*")) {
        const update = applyMiddleware(_middlewares)(name, events, action, _model);
        if (update) {
          setState(update);
          Store.emitChange();
        }
      }
      return true;
    })
  });
  Store.setMaxListeners(999);
  return Store;
};
export default CreateStore;